/**
 * Tutte le commesse sulla rete, in una tela sola (S17, L-4).
 *
 * Il supervisore distribuisce il turno e non aveva un posto dove vedere
 * **dove** sulla rete c'e' lavoro oggi: il pool era una tabella e mezzo
 * schermo vuoto. Qui ogni commessa e' la sua linea, col tono dello stato che
 * ha in tabella; il clic la sceglie nella tabella, la tabella la illumina qui.
 * Stessa proiezione e stessi gettoni della mappa della linea.
 */
import { useEffect, useState } from 'react'

import { useT } from '../i18n/LocaleProvider'
import { geometry, type JobGeometry } from '../inspections'
import type { MapPoint } from '../lineMap'
import { pathOf, projectionOf, SIDE } from '../map/projection'
import type { Commessa } from '../types'
import './poolMap.css'

export function PoolMap({
  rows,
  tones,
  chosen,
  onPick,
}: {
  rows: Commessa[]
  /** Il tono dello stato di ogni commessa, per identita': lo sa la tabella. */
  tones: Record<string, string>
  chosen: string | null
  onPick: (inspectionId: string) => void
}): JSX.Element | null {
  const t = useT()
  const [shapes, setShapes] = useState<JobGeometry[] | null>(null)
  useEffect(() => {
    let alive = true
    geometry()
      .then((found) => alive && setShapes(found))
      .catch(() => alive && setShapes([]))
    return () => {
      alive = false
    }
  }, [])
  if (shapes === null) return <p className="tenue">{t('pool.mapReading')}</p>
  const shown = shapes.filter((one) => one.lines.length > 0 && rows.some((row) => row.inspection_id === one.inspection_id))
  if (shown.length === 0) return <p className="tenue">{t('pool.mapEmpty')}</p>
  const points: MapPoint[] = shown.flatMap((one) => one.lines.flat())
  const projection = projectionOf(points)
  return (
    <svg viewBox={`0 0 ${SIDE} ${SIDE}`} className="pool-map" role="group" aria-label={t('pool.mapTitle', { n: shown.length })}>
      {shown.map((one) => {
        const tone = tones[one.inspection_id] ?? 'ink-3'
        const on = one.inspection_id === chosen
        const first = one.lines[0]![0]!
        const x = projection.x(first)
        const y = projection.y(first)
        return (
          <g
            key={one.inspection_id}
            role="link"
            tabIndex={0}
            className={on ? 'pool-job on' : 'pool-job'}
            style={{ color: `var(--${tone})` }}
            aria-label={t('pool.mapJob', { id: one.external_inspection_id })}
            onClick={() => onPick(one.inspection_id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onPick(one.inspection_id)
              }
            }}
          >
            {one.lines.map((line, index) =>
              line.length === 1 ? (
                <circle key={index} cx={projection.x(line[0]!)} cy={projection.y(line[0]!)} r={1.2} />
              ) : (
                <path key={index} d={pathOf(line, projection)} />
              ),
            )}
            <text x={x + 2.4} y={y - 1.6} className="pool-map-label">
              {one.external_inspection_id}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
